/**
 * Turning the tickets API into inbox rows.
 *
 * The screens ran on fixtures until now, and the swap has one rule worth
 * stating: **an empty inbox must look empty.** A fixture left in as a fallback,
 * or a placeholder row shown when a fetch returns nothing, is how a system that
 * is not receiving any tickets looks exactly like one that is working. The
 * empty state says so plainly instead.
 *
 * Status is derived from the RUN rather than the ticket, because the ticket's
 * status is Zendesk's opinion and the run's is ours. What ops needs to see is
 * what Zoye did, and whether it is waiting on them.
 */

import { INTENT_DESCRIPTIONS } from './intent-labels';

export interface ApiRun {
  id: string;
  mode: 'shadow' | 'assisted' | 'auto';
  status: string;
  escalated: boolean;
  escalationReason: string | null;
  confidence: number | null;
  draftReply: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface ApiTicket {
  id: string;
  externalId: string;
  intent: string | null;
  customerName: string | null;
  customerEmail: string | null;
  opportunityCents: number | null;
  status: string;
  openedAt: string;
  brand: { slug: string; name: string };
  runs: ApiRun[];
  /** Zendesk's own status and creation time, read live; null when unavailable. */
  zendesk?: { status: string | null; createdAt: string | null } | null;
}

export type RowStatus =
  | 'needs_approval'
  | 'drafted'
  | 'escalated'
  | 'auto_closed'
  | 'awaiting_agent';

export interface InboxRow {
  id: string;
  customer: string | null;
  ticketRef: string;
  /** The Zendesk ticket id, for the link to Zendesk. */
  externalId: string;
  brand: string;
  intent: string;
  intentSlug: string | null;
  mode: 'shadow' | 'assisted' | 'auto';
  /** Null when the router never committed — rendered as "—", not as 0%. */
  confidence: number | null;
  opportunityCents: number | null;
  status: RowStatus;
  /** Present on an escalation, so the list explains itself without a click. */
  reason: string | null;
  hasDraft: boolean;
  openedAt: string;
  /** When the ticket was created in Zendesk, else when Zoye first saw it. */
  createdAt: string;
  /** Zendesk's status (new, open, pending, hold, solved, closed); null when not read. */
  zendeskStatus: string | null;
}

/**
 * A ticket's age: hours for the first two days, then days (Deivydas
 * 2026-10-14). "<1h" under an hour.
 */
export function ticketAge(createdAt: string, now: Date = new Date()): string {
  const hours = Math.floor((now.getTime() - new Date(createdAt).getTime()) / 3_600_000);
  if (!Number.isFinite(hours) || hours < 1) return '<1h';
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Human-readable, falling back to the raw slug rather than inventing a label. */
export function intentLabel(slug: string | null): string {
  if (!slug) return 'Unclassified';
  const description = INTENT_DESCRIPTIONS[slug];
  return description ?? slug.replace(/_/g, ' ');
}

function statusOf(run: ApiRun | undefined, ticketStatus: string): RowStatus {
  // TICKET-level escalation outranks whatever the latest run did (pre-pilot
  // QA #12): the console hand-off sets it without a new run, and a ticket a
  // human owns must show in the Escalated view — before this it kept showing
  // "Drafted" (or "Closed") and was silently lost from the queue.
  if (ticketStatus === 'escalated') return 'escalated';
  // No run at all means intake has not reached it. That is a real state during
  // a backlog and it should not be dressed up as "drafted".
  if (!run) return 'awaiting_agent';
  if (run.escalated) return 'escalated';
  if (run.status === 'awaiting_approval') return 'needs_approval';
  if (run.status === 'sent' || ticketStatus === 'resolved' || ticketStatus === 'closed') {
    return 'auto_closed';
  }
  if (run.status === 'drafted') return 'drafted';
  return 'awaiting_agent';
}

export function toInboxRows(tickets: readonly ApiTicket[]): InboxRow[] {
  return tickets.map((ticket) => {
    const run = ticket.runs[0];
    return {
      id: ticket.id,
      customer: ticket.customerName ?? ticket.customerEmail ?? null,
      ticketRef: `#${ticket.externalId}`,
      externalId: ticket.externalId,
      brand: ticket.brand.name,
      intent: intentLabel(ticket.intent),
      intentSlug: ticket.intent,
      mode: run?.mode ?? 'shadow',
      confidence: run?.confidence ?? null,
      opportunityCents: ticket.opportunityCents,
      status: statusOf(run, ticket.status),
      reason: run?.escalated ? run.escalationReason : null,
      hasDraft: Boolean(run?.draftReply),
      openedAt: ticket.openedAt,
      createdAt: ticket.zendesk?.createdAt ?? ticket.openedAt,
      zendeskStatus: ticket.zendesk?.status ?? null,
    };
  });
}

/**
 * The brand list for the views rail, counted from the rows on screen.
 *
 * Sorted by count so the busiest brand sits on top, name as the tiebreak so
 * the list is stable while counts are equal.
 */
export function countByBrand(rows: readonly InboxRow[]): Array<{ brand: string; count: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.brand, (counts.get(row.brand) ?? 0) + 1);
  return [...counts.entries()]
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count || a.brand.localeCompare(b.brand));
}

/**
 * Counts for the filter chips.
 *
 * Computed from the rows on screen rather than fetched separately, so the
 * number on a chip always matches what clicking it shows.
 */
export function countByStatus(rows: readonly InboxRow[]): Record<RowStatus, number> {
  const counts: Record<RowStatus, number> = {
    needs_approval: 0,
    drafted: 0,
    escalated: 0,
    auto_closed: 0,
    awaiting_agent: 0,
  };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}
